import React, { createContext, useContext, useMemo, useState, useEffect } from 'react';
import { ThemeProvider, createTheme, alpha } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import '../theme/darkModeFix.css';

/** white | light | dark */
const MODES = ['white', 'light', 'dark'];

const ThemeContext = createContext({
  mode: 'light',
  setMode: () => {},
  toggleTheme: () => {},
  cycleTheme: () => {},
  isDark: false,
  modes: MODES
});

export const useThemeMode = () => useContext(ThemeContext);

const normalizeMode = (value) => {
  if (value === 'dark') return 'dark';
  if (value === 'white') return 'white';
  if (value === 'light') return 'light';
  return 'light';
};

const getStoredMode = () => normalizeMode(localStorage.getItem('erp_theme_mode'));

const paletteFor = (mode) => {
  if (mode === 'dark') {
    return {
      mode: 'dark',
      primary: { main: '#42a5f5', light: '#80d6ff', dark: '#0077c2' },
      secondary: { main: '#f48fb1', light: '#ffc1e3', dark: '#bf5f82' },
      success: { main: '#66bb6a' },
      warning: { main: '#ffa726' },
      error: { main: '#ef5350' },
      info: { main: '#29b6f6' },
      text: {
        primary: '#f5f5f5',
        secondary: '#bdbdbd'
      },
      background: {
        default: '#121826',
        paper: '#1b2434'
      },
      divider: 'rgba(255,255,255,0.12)'
    };
  }

  if (mode === 'white') {
    return {
      mode: 'light',
      primary: { main: '#1976d2', light: '#42a5f5', dark: '#1565c0' },
      secondary: { main: '#dc004e' },
      success: { main: '#2e7d32' },
      warning: { main: '#ed6c02' },
      error: { main: '#d32f2f' },
      info: { main: '#0288d1' },
      text: {
        primary: '#1a1a1a',
        secondary: '#555555'
      },
      background: {
        default: '#ffffff',
        paper: '#ffffff'
      },
      divider: 'rgba(0,0,0,0.12)'
    };
  }

  // light — soft colorful page
  return {
    mode: 'light',
    primary: { main: '#1976d2', light: '#42a5f5', dark: '#1565c0' },
    secondary: { main: '#dc004e' },
    success: { main: '#2e7d32' },
    warning: { main: '#ed6c02' },
    error: { main: '#d32f2f' },
    info: { main: '#0288d1' },
    text: {
      primary: '#1a1a1a',
      secondary: '#555555'
    },
    background: {
      default: '#eef3fb',
      paper: '#ffffff'
    },
    divider: 'rgba(0,0,0,0.12)'
  };
};

export const AppThemeProvider = ({ children }) => {
  const [mode, setModeState] = useState(getStoredMode);

  const setMode = (next) => setModeState(normalizeMode(next));

  useEffect(() => {
    localStorage.setItem('erp_theme_mode', mode);
    document.documentElement.setAttribute('data-theme', mode);
    document.body.setAttribute('data-theme', mode);
    document.body.classList.toggle('erp-dark', mode === 'dark');
    document.body.classList.toggle('erp-white', mode === 'white');
    document.body.classList.toggle('erp-light', mode === 'light');
  }, [mode]);

  const toggleTheme = () =>
    setModeState((prev) => (prev === 'dark' ? 'light' : 'dark'));

  const cycleTheme = () =>
    setModeState((prev) => {
      const i = MODES.indexOf(prev);
      return MODES[(i + 1) % MODES.length];
    });

  const theme = useMemo(() => {
    const palette = paletteFor(mode);
    const isDark = mode === 'dark';

    return createTheme({
      palette,
      typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h4: { fontWeight: 600 },
        h5: { fontWeight: 600 },
        h6: { fontWeight: 600 }
      },
      shape: { borderRadius: 10 },
      components: {
        MuiCssBaseline: {
          styleOverrides: {
            body: {
              backgroundColor: palette.background.default,
              color: palette.text.primary
            },
            '#root': {
              minHeight: '100vh',
              backgroundColor: palette.background.default
            }
          }
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              backgroundImage: 'none'
              // bgcolor left to palette / sx so tinted colorful cards work
            }
          }
        },
        MuiCard: {
          styleOverrides: {
            root: {
              borderRadius: 12,
              backgroundImage: 'none',
              backgroundColor: palette.background.paper,
              boxShadow: isDark
                ? '0 4px 16px rgba(0,0,0,0.45)'
                : '0 2px 10px rgba(25,118,210,0.08)'
            }
          }
        },
        MuiButton: {
          styleOverrides: {
            root: { textTransform: 'none', borderRadius: 8, fontWeight: 600 }
          }
        },
        MuiTableCell: {
          styleOverrides: {
            head: {
              fontWeight: 700,
              backgroundColor: isDark
                ? alpha('#42a5f5', 0.12)
                : alpha('#1976d2', 0.08)
            }
          }
        },
        MuiChip: {
          styleOverrides: {
            root: { fontWeight: 600 }
          }
        },
        MuiTab: {
          styleOverrides: {
            root: {
              fontWeight: 600,
              '&.Mui-selected': { color: palette.primary.main }
            }
          }
        }
      }
    });
  }, [mode]);

  return (
    <ThemeContext.Provider
      value={{
        mode,
        setMode,
        toggleTheme,
        cycleTheme,
        isDark: mode === 'dark',
        modes: MODES
      }}
    >
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
